import React, { useMemo, useState, useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FrontLayout from "../../../../../components/layout-new-theme/FrontLayout";
import { createTypographyHelper } from "../../../../../utils/typography";
import { cn } from "../../../../../utils/classnames";
import {
  SectionTitleRow,
  LabelCell,
  ValueText,
  ValueCell,
  ComparisonRow,
  ValuePaxText
} from "./components/TableCells";
import VehicleCell from "./components/VehicleCell";
import PriceDetailsCell from "./components/PriceDetailsCell";
import ImportantNotice from "./components/ImportantNotice";
import { useHistory } from "react-router-dom";
import { BackIcon, CancelIcon, ArrowCircleDownIcon, ArrowCircleUpIcon } from "../../../../../components/icons";
import { getLocationDetail } from "../../utils";
import moment from "moment";
import { DATE_TIME_FORMAT } from "../../constants"
import CancellationFeeInformation from "./components/CancellationFeeInformation";
import SkeletonLoading from "../SkeletonLoading";
import CancellationPolicyModal from "./components/CancellationPolicyModal";
import SubmitEditConfirmationModal from "./components/SubmitEditConfirmationModal";
import DiscardEditConfirmationModal from "./components/DiscardEditConfirmationModal";
import SuccessSubmitEditBookingModal from "./components/SuccessSubmitEditBookingModal";
import SaveDraftConfirmationModal from "./components/SaveDraftConfirmationModal";

const BookingEditReview = ({
  simulationData: simulationDataProp = null,
  idBookingProp = "",
  embedded = false,
  onBackToEdit = null,
  onSubmitModificationRequest = null,
  isSubmittingModification = false,
}) => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const getTypographyClass = createTypographyHelper(i18n.language);
  const { id_booking } = useParams();
  const location = useLocation();
  const simulationData = simulationDataProp || location?.state?.simulationData || null;
  const bookingIdParam = idBookingProp || id_booking;
  const changes = simulationData?.changesIdentification || {};
  const editFeeCalculation = simulationData?.editFeeCalculation || {};
  const oldBooking = simulationData?.oldBooking || {};
  const calculatedEditBooking = simulationData?.calculatedEditBooking || {};
  const bookingPaymentStatus = simulationData?.paymentStatus || "";
  const userDeferredPaymentEligible = simulationData?.deferredPayment || false;
  const userRole = simulationData?.userRole || "individual";
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showSubmitSuccessModal, setShowSubmitSuccessModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!simulationData) {
    return (
      <FrontLayout>
        <div className="tw:bg-[linear-gradient(180deg,#f6f6f6_0%,#ffffff_100%)] tw:pt-[152px] tw:pb-16">
          <div className="tw:px-0 tw:lg:px-[70px] tw:max-w-[1300px] tw:mx-auto">
            <SkeletonLoading />
          </div>
        </div>
      </FrontLayout>
    );
  }

  if (currentLanguage === "en") {
    moment.locale("en");
  } else {
    moment.locale("ja");
  }

  const formatDuration = (duration) => {
    if (!duration) return "-";

    let totalMinutes = 0;
    const [h = "0", m = "0"] = duration.split(":");
    totalMinutes = Number(h) * 60 + Number(m);
    const decimalHours = totalMinutes / 60;
    const roundedToHalfHour = Math.ceil(decimalHours * 2) / 2;

    return `${roundedToHalfHour}${t("editBooking.BookingReviewPage.generalInformation.hours")}`;
  };

  const minuteToHoursFormat = (minutes) => {
    if (!minutes) return "-";

    const decimalHours = minutes / 60;
    const roundedToHalfHour = Math.ceil(decimalHours * 2) / 2;

    return roundedToHalfHour;
  }

  const getChangeData = (key) => {
    const item = changes?.[key] || {};
    return {
      newValue: item?.new,
      oldValue: item?.old,
      changed: Boolean(item?.changed),
    };
  };

  const formatTransferType = (value, isAirportTransfer) => {
    if (value === "byTheHour") return t("editBooking.BookingReviewPage.generalInformation.bookingType.byTheHour");
    if (value === "specifiedDestination") return isAirportTransfer ? t("editBooking.BookingReviewPage.generalInformation.bookingType.airportTransfer") : t("editBooking.BookingReviewPage.generalInformation.bookingType.specifiedDestination");
    return "-";
  };

  const formatPaxGroup = (paxList = [], t) => {
    if (!Array.isArray(paxList)) return "-";
    const text = paxList
      .filter((item) => Number(item?.paxcount || 0) > 0)
      .map((item) => `${t(`my_page.bookings_page.booking_item.${item?.paxtype}`, { defaultValue: item?.paxtype || "-" })}: ${item?.paxcount}`)
      .join(", ");
    return text || "-";
  };

  const formatLuggageGroup = (luggageList = [], t) => {
    if (!Array.isArray(luggageList)) return "-";
    const text = luggageList
      .filter((item) => Number(item?.luggagecount || 0) > 0)
      .map((item) => {
        const typeKey = String(item?.luggagetype || "").toLowerCase().includes("check") ? "check_in" : "cabin";
        const type = t(`my_page.bookings_page.booking_item.${typeKey}`);
        return `${type}: ${item?.luggagecount}`;
      })
      .join(", ");
    return text || "-";
  };

  const formatLocation = (locationData) => {
    if (!locationData) return "-";
    return locationData?.formatted_address || locationData?.label || locationData?.value || "-";
  };

  const formatDestinations = (destinationList = [], changed = false) => {
    if (!Array.isArray(destinationList) || destinationList.length === 0) {
      return "-";
    }

    return destinationList.map((item, index) => {
      const location = getLocationDetail(item);
      if (!location) return null;

      return (
        <ValueText
          key={index}
          text={location.name}
          secondary={location.address}
          bold={changed}
        />
      );
    });
  };

  const getVehiclePriceDetails = (vehicle) => {
    if (!vehicle) return [];

    const priceDetails = [
      {
        label: t("editBooking.BookingReviewPage.pricing.charterCost"),
        type: "price",
        value: formatJPY(vehicle.total)
      },
      {
        label: t("editBooking.BookingReviewPage.pricing.lateNightFee"),
        type: "price",
        value: formatJPY(vehicle.nightPrice)
      }
    ];

    if (vehicle.discount?.amount > 0) {
      if (vehicle.discount?.campaignEligible) {
        priceDetails.push({
          label: t("editBooking.BookingReviewPage.pricing.campaignDiscount", { percentage: vehicle.discount?.rate }),
          type: "discount",
          value: `-${formatJPY(vehicle.discount?.amount)}`
        });
      } else if (vehicle.discount?.iataEligible) {
        priceDetails.push({
          label: t("editBooking.BookingReviewPage.pricing.commissionDiscount", { percentage: vehicle.discount?.rate }),
          type: "discount",
          value: `-${formatJPY(vehicle.discount?.amount)}`
        });
      }
    }

    priceDetails.push(
      {
        label: t("editBooking.BookingReviewPage.pricing.tollFee"),
        type: "price",
        value: formatJPY((vehicle.tollFare || 0) + (vehicle.travelAreaPrice || 0)),
      },
      {
        label: t("editBooking.BookingReviewPage.pricing.longDistanceSurcharge"),
        type: "price",
        value: formatJPY(vehicle.longDistanceSurcharge || 0),
      },
      {
        label: t("editBooking.BookingReviewPage.pricing.addOn"),
        type: "price",
        value: formatJPY(vehicle.childSeatPrice)
      }
    );

    return priceDetails;
  };

  const normalizeVehiclePricing = (vehicle) => {
    if (!vehicle) return null;

    return {
      total: Number(vehicle?.total || 0),
      tollFare: Number(vehicle?.tollFare || 0),
      travelAreaPrice: Number(vehicle?.travelAreaPrice || 0),
      nightPrice: Number(vehicle?.nightPrice || 0),
      longDistanceSurcharge: Number(vehicle?.longDistanceSurcharge || 0),
      childSeatPrice: Number(vehicle?.childSeatPrice || 0),
      baseprice: Number(vehicle?.baseprice || 0),
      hourlyprice: Number(vehicle?.hourlyprice || 0),
      discount: Number(vehicle?.discount?.rate || 0),
      totalDiscount: Number(vehicle?.discount?.amount || 0),
    };
  };

  const isVehiclePricingChanged = (beforeVehicle, afterVehicle) => (
    JSON.stringify(normalizeVehiclePricing(beforeVehicle)) !== JSON.stringify(normalizeVehiclePricing(afterVehicle))
  );

  const getVehicleAddonDetails = (vehicle) => {
    if (!vehicle || !Array.isArray(vehicle.childSeat)) return [];
    const getChildSeatTypeLabel = (type, count) => {
      const typeKey = String(type || "").toLowerCase();
      return t(`editBooking.BookingReviewPage.additionalInformation.childSeatType.${typeKey}`, {
        count,
        defaultValue: `${type || "-"} x${count ?? 0}`,
      });
    };

    const addons = vehicle.childSeat
      .filter((seat) => Number(seat?.childSeatCount || 0) > 0)
      .map((seat) => ({
        label: getChildSeatTypeLabel(seat?.childSeatType, seat?.childSeatCount)
      }));
    return addons.length > 0 ? addons : [{ label: t("editBooking.BookingReviewPage.none") }];
  };


  const formatMeetAssist = (meetAssistList = []) => {
    const arrival = meetAssistList.find((item) => item?.meetAssistType === "arrival")?.meetAssistName;
    const departure = meetAssistList.find((item) => item?.meetAssistType === "departure")?.meetAssistName;

    const hasArrival = Boolean(arrival && arrival !== "none");
    const hasDeparture = Boolean(departure && departure !== "none");

    if (!hasArrival && !hasDeparture) {
      return { title: t("editBooking.BookingReviewPage.none") };
    }

    let text = "";
    if (hasArrival) {
      text += `${t(`editBooking.BookingReviewPage.additionalInformation.meetAssistType.${arrival}`)} (${t(`editBooking.BookingReviewPage.additionalInformation.meetAssistLoc.arrival`)})`;
    }
    if (hasDeparture) {
      if (text) text += "\n";
      text += `${t(`editBooking.BookingReviewPage.additionalInformation.meetAssistType.${departure}`)} (${t(`editBooking.BookingReviewPage.additionalInformation.meetAssistLoc.departure`)})`;
    }

    return {
      title: text,
    };
  };

  const formatChildSeatSummary = (bookingInfo = []) => {
    if (!Array.isArray(bookingInfo) || bookingInfo.length === 0) return t("editBooking.BookingReviewPage.none");
    const getChildSeatTypeLabel = (type, count) => {
      const typeKey = String(type || "").toLowerCase();
      return t(`editBooking.BookingReviewPage.additionalInformation.childSeatType.${typeKey}`, {
        count,
        defaultValue: `${type || "-"} x${count ?? 0}`,
      });
    };

    const summaries = bookingInfo
      .map((item, index) => {
        const seats = Array.isArray(item?.childSeat)
          ? item.childSeat
            .filter((seat) => Number(seat?.childSeatCount || 0) > 0)
            .map((seat) => getChildSeatTypeLabel(seat?.childSeatType, seat?.childSeatCount))
          : [];

        if (seats.length === 0) return null;
        return `Vehicle ${index + 1}: ${seats.join(", ")}`;
      })
      .filter(Boolean);

    return summaries.length ? summaries.join(" | ") : t("editBooking.BookingReviewPage.none");
  };

  const formatVehicleName = (vehicleInfo) => {
    if (!vehicleInfo) return "-";
    return (
      vehicleInfo?.vehicleTypeName ||
      vehicleInfo?.vehicleTypeID?.typename ||
      vehicleInfo?.vehicleType ||
      vehicleInfo?.carType ||
      "-"
    );
  };

  const formatVehicleTagName = (vehicleInfo) => {
    if (!vehicleInfo) return "-";

    const vehicleTypeKey = String(vehicleInfo?.vehicleTypeName || "").trim().replace(/\s+/g, ".");
    if (!vehicleTypeKey) return "-";

    const vehicleTypeTranslations = t("VehicleTypeName", { returnObjects: true }) || {};
    const groupedTag = vehicleTypeTranslations?.[`${vehicleTypeKey}.Tag`];

    if (groupedTag) {
      return String(groupedTag).toUpperCase();
    }

    const translatedTag = t(`${vehicleTypeKey}.Tag`, {
      defaultValue: t(`VehicleTypeName.${vehicleTypeKey}.Tag`, {
        defaultValue: "-",
      }),
    });

    return translatedTag ? translatedTag.toUpperCase() : "-";
  };

  const formatVehicleTypeName = (vehicleInfo) => {
    if (!vehicleInfo) return "-";

    const vehicleTypeKey = String(vehicleInfo?.vehicleTypeName || "").trim().replace(/\s+/g, ".");
    if (!vehicleTypeKey) return "-";

    const vehicleTypeTranslations = t("VehicleTypeName", { returnObjects: true }) || {};
    const groupedName = vehicleTypeTranslations?.[vehicleTypeKey];

    if (groupedName) {
      return String(groupedName);
    }

    return t(vehicleTypeKey, {
      defaultValue: t(`VehicleTypeName.${vehicleTypeKey}`, {
        defaultValue: vehicleInfo?.vehicleTypeName || "-",
      }),
    });
  };

  const formatJPY = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value)) || value === 0) return "-";
    return `${Number(value).toLocaleString()}${t("editBooking.BookingReviewPage.jpy")}`;
  };

  const bookingTypeChange = getChangeData("bookingType");
  const paxChange = getChangeData("pax");
  const luggageChange = getChangeData("luggage");
  const pickupLocationChange = getChangeData("pickupLocation");
  const destinationsChange = getChangeData("destinations");
  const durationChange = getChangeData("duration");
  const billedDurationChange = getChangeData("billedDuration");
  const travelAreaChange = getChangeData("travelArea");
  const travelDateChange = getChangeData("travelDate");
  const pickupTimeChange = getChangeData("pickupTime");
  const pickupDateTimeChange = getChangeData("pickupDateTime");
  const customerDropoffDateTimeChange = getChangeData("customerDropoffDateTime");
  const pickupFlightNoChange = getChangeData("pickupFlightNo");
  const pickupTerminalChange = getChangeData("pickupTerminal");
  const pickupSignHonorificChange = getChangeData("pickupSignHonorific");
  const pickupSignChange = getChangeData("pickupSign");
  const dropoffFlightNoChange = getChangeData("dropoffFlightNo");
  const dropoffTerminalChange = getChangeData("dropoffTerminal");
  const meetAssistChange = getChangeData("meetAssist");
  const bookingInfoChange = getChangeData("bookingInfo");
  const specialRequestChange = getChangeData("message");
  const passengerNameChange = getChangeData("passengerName");
  const passengerTitleChange = getChangeData("passengerPronounced");
  const passengerPrefixChange = getChangeData("passengerPrefixNumber");
  const passengerPhoneChange = getChangeData("passengerPhoneNumber");

  const totalCharterCostChange = getChangeData("totalCharterCost");
  const totalTollFeeChange = getChangeData("totalTollFee");
  const totalNightPriceChange = getChangeData("totalNightPrice");
  const totalLongDistanceSurchargeChange = getChangeData("totalLongDistanceSurcharge");
  const totalMeetAssistPriceChange = getChangeData("totalMeetAssistPrice");
  const totalChildSeatPriceChange = getChangeData("totalChildSeatPrice");
  const totalPriceChange = getChangeData("totalPrice");

  const formatPassengerName = (title, name) => {
    const fullName = `${title || ""} ${name || ""}`.trim();
    return fullName || "-";
  };

  const formatPassengerPhone = (prefix, phone) => {
    if (!phone) return "-";
    return `(${prefix || "-"}) ${phone}`.trim();
  };

  const formatMessage = (message) => {
    if (!message) return "-";
    if (!message[0].note) return "-";
    return message[0].note;
  }

  const hasPickupFlightInfo = Boolean(
    pickupFlightNoChange?.oldValue ||
    pickupFlightNoChange?.newValue ||
    pickupTerminalChange?.oldValue ||
    pickupTerminalChange?.newValue ||
    pickupSignChange?.oldValue ||
    pickupSignChange?.newValue
  );

  const hasDropoffFlightInfo = Boolean(
    dropoffFlightNoChange?.oldValue ||
    dropoffFlightNoChange?.newValue ||
    dropoffTerminalChange?.oldValue ||
    dropoffTerminalChange?.newValue
  );

  const isAirportTransfer = Boolean(
    oldBooking?.isAirportTransfer ||
    calculatedEditBooking?.isAirportTransfer ||
    bookingTypeChange?.oldValue === "specifiedDestination" ||
    bookingTypeChange?.newValue === "specifiedDestination"
  );

  const isFromAirportTransfer = Boolean(
    oldBooking?.isFromAirport ||
    calculatedEditBooking?.isFromAirport ||
    hasPickupFlightInfo
  );

  const isToAirportTransfer = Boolean(
    oldBooking?.isToAirport ||
    calculatedEditBooking?.isToAirport ||
    hasDropoffFlightInfo
  );

  const pickupFlightLabelType = (() => {
    const fromAirport = oldBooking.isFromAirport || calculatedEditBooking.isFromAirport;
    const fromStation = oldBooking.isFromStation || calculatedEditBooking.isFromStation;
    if (fromAirport && fromStation) return 'both';
    if (fromStation) return 'station';
    return 'airport';
  })();

  const dropoffFlightLabelType = (() => {
    const toAirport = oldBooking.isToAirport || calculatedEditBooking.isToAirport;
    const toStation = oldBooking.isToStation || calculatedEditBooking.isToStation;
    if (toAirport && toStation) return 'both';
    if (toStation) return 'station';
    return 'airport';
  })();

  const beforeVehicles = Array.isArray(bookingInfoChange?.oldValue) ? bookingInfoChange.oldValue : [];
  const afterVehicles = Array.isArray(bookingInfoChange?.newValue) ? bookingInfoChange.newValue : [];
  const maxVehicles = Math.max(beforeVehicles.length, afterVehicles.length, 1);

  const vehiclePairs = (() => {
    const pairs = [];
    const usedAfterIndices = new Set();

    for (let i = 0; i < beforeVehicles.length; i++) {
      const vBefore = beforeVehicles[i];
      const beforeTypeName = formatVehicleTypeName(vBefore);
      const matchIndex = afterVehicles.findIndex((vAfter, j) =>
        !usedAfterIndices.has(j) && formatVehicleTypeName(vAfter) === beforeTypeName
      );
      if (matchIndex !== -1) {
        usedAfterIndices.add(matchIndex);
        pairs.push({ before: vBefore, after: afterVehicles[matchIndex] });
      } else {
        pairs.push({ before: vBefore, after: null });
      }
    }

    // Second pass: pair unmatched "before" entries with leftover "after" vehicles positionally
    const unmatchedAfter = afterVehicles.filter((_, j) => !usedAfterIndices.has(j));
    let unmatchedIdx = 0;
    for (let i = 0; i < pairs.length; i++) {
      if (pairs[i].after === null && unmatchedIdx < unmatchedAfter.length) {
        pairs[i].after = unmatchedAfter[unmatchedIdx++];
      }
    }
    while (unmatchedIdx < unmatchedAfter.length) {
      pairs.push({ before: null, after: unmatchedAfter[unmatchedIdx++] });
    }

    return pairs;
  })();

  const passengerText = simulationData?.bookingCustomer?.name || simulationData?.passengerName || "Passenger";

  const oldPassengerName = passengerNameChange?.oldValue ?? oldBooking?.passengerName ?? simulationData?.bookingCustomer?.name;
  const newPassengerName = passengerNameChange?.newValue ?? calculatedEditBooking?.passengerName ?? simulationData?.passengerName;
  const oldPassengerTitle = passengerTitleChange?.oldValue ?? oldBooking?.passengerPronounced;
  const newPassengerTitle = passengerTitleChange?.newValue ?? calculatedEditBooking?.passengerPronounced;
  const oldPassengerPrefix = passengerPrefixChange?.oldValue ?? oldBooking?.passengerPrefixNumber;
  const newPassengerPrefix = passengerPrefixChange?.newValue ?? calculatedEditBooking?.passengerPrefixNumber;
  const oldPassengerPhone = passengerPhoneChange?.oldValue ?? oldBooking?.passengerPhoneNumber;
  const newPassengerPhone = passengerPhoneChange?.newValue ?? calculatedEditBooking?.passengerPhoneNumber;

  // Highlight if either value changed and values are actually different
  const passengerNameChanged = (passengerNameChange?.changed || passengerTitleChange?.changed)
    && (formatPassengerName(oldPassengerTitle, oldPassengerName) !== formatPassengerName(newPassengerTitle, newPassengerName));
  const passengerPhoneChanged = (passengerPhoneChange?.changed || passengerPrefixChange?.changed)
    && (formatPassengerPhone(oldPassengerPrefix, oldPassengerPhone) !== formatPassengerPhone(newPassengerPrefix, newPassengerPhone));

  const bookingIdText = simulationData?.bookingId || bookingIdParam ? `Booking ID #${simulationData?.bookingId || bookingIdParam}` : "Booking ID";

  const beforePrice = Number(totalPriceChange?.oldValue || 0);
  const afterPrice = Number(totalPriceChange?.newValue || 0);
  const diffPrice = afterPrice - beforePrice;

  const totalFees = (editFeeCalculation?.totalFee || 0) + (editFeeCalculation?.meetAssistCancelFee?.arrival || 0) + (editFeeCalculation?.meetAssistCancelFee?.departure || 0);
  const newTotalWithFees = Number(calculatedEditBooking?.totalPrice || 0) + totalFees;
  const needsCharge = oldBooking.paymentStatus !== "CHARGE_PENDING" && Number(oldBooking?.totalPrice || 0) < newTotalWithFees;
  const chargeAmount = needsCharge ? newTotalWithFees - Number(oldBooking?.totalPrice || 0) : 0;

  const handleSubmitModification = async () => {
    if (!onSubmitModificationRequest) {
      setShowSubmitModal(false);
      return;
    }

    const isSuccess = await onSubmitModificationRequest();
    if (isSuccess) {
      setShowSubmitModal(false);
      setShowSubmitSuccessModal(true);
    }
  };

  const handleSaveDraft = async () => {
    if (!onSubmitModificationRequest) {
      setShowDraftModal(false);
      return;
    }

    const isSuccess = await onSubmitModificationRequest({ saveAsDraft: true });
    if (isSuccess) {
      setShowDraftModal(false);
      goToBookingDetail();
    }
  };

  const goToBookingDetail = () => {
    if (bookingIdParam) {
      history.push(`/booking-detail/${bookingIdParam}`);
      return;
    }
    history.push("/my-page/bookings");
  };

  const handleCloseSubmitSuccessModal = () => {
    setShowSubmitSuccessModal(false);
    goToBookingDetail();
  };

  const handleDiscardEdit = () => {
    setShowDiscardModal(false);
    goToBookingDetail();
  };

  const getOldBookingPaymentInfo = (booking, deferredPayment) => {
    if (deferredPayment) {
      if (booking.paymentStatus !== "CHARGE_PENDING") {
        if (booking.confirmWithEditedFee) {
          return t("editBooking.BookingReviewPage.pricing.addedToMonthlyInvoiceWithEditedFee");
        } else {
          return t("editBooking.BookingReviewPage.pricing.addedToMonthlyInvoice");
        }
      }
    } else {
      if (booking.paymentStatus !== "CHARGE_PENDING") {
        if (booking.editedFeeType === "CHARGE_EDITED_FEE") {
          return t("editBooking.BookingReviewPage.pricing.chargedWithEditedFee");
        } else {
          return t("editBooking.BookingReviewPage.pricing.charged");
        }
      }
    }

    return t("editBooking.BookingReviewPage.pricing.notCharged");
  }

  const getPickupSignText = (honorific, name) => {
    if (!name) return "-";
    if (honorific === "-") return name;

    return `${honorific} ${name}`;
  }

  const content = (
    <div className="tw:bg-[linear-gradient(180deg,#f6f6f6_0%,#ffffff_100%)] tw:pt-10 tw:pb-16 tw:text-[#212529] tw:font-hanken-grotesk">
      <div className="tw:w-full tw:mx-auto tw:[--gx:1.5rem] tw:px-[calc(var(--gx)/2)] tw:sm:max-w-[540px] tw:md:max-w-[720px] tw:lg:max-w-[960px] tw:xl:max-w-[1140px] tw:2xl:max-w-[1320px] tw:max-w-[1300px] tw:flex tw:flex-col tw:gap-[42px]">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-6">
          {embedded ? (
            <button
              type="button"
              onClick={onBackToEdit}
              className={cn(
                "tw:!inline-flex tw:!items-center tw:!gap-[10px] tw:!rounded-[5px] tw:!border tw:!border-[#e8e8e8] tw:!bg-[#e8e8e8] tw:!px-3 tw:!py-2 tw:!text-[#282828] tw:!no-underline",
                getTypographyClass(13)
              )}
            >
              <BackIcon color="#8D8D8D" width={7} height={10} />
              <span className="tw:!font-hanken-grotesk tw:!text-[#282828]">{t("editBooking.BookingReviewPage.header.back")}</span>
            </button>
          ) : (
            <Link
              to={id_booking ? `/booking/${id_booking}/edit` : "/my-page/bookings"}
              className="tw:!inline-flex tw:!items-center tw:!gap-[10px] tw:!rounded-[5px] tw:!border tw:!border-[#e8e8e8] tw:!bg-[#e8e8e8] tw:!px-3 tw:!py-2 tw:!text-[#282828] tw:!no-underline"
            >
              <BackIcon color="#8D8D8D" width={7} height={10} />
              <span className="tw:!font-hanken-grotesk tw:!text-[#282828]">{t("editBooking.BookingReviewPage.header.back")}</span>
            </Link>
          )}
          <div className="tw:flex tw:flex-col">
            <div className="tw:font-hanken-grotesk tw:font-medium tw:text-[#7c7b7b]">
              {t("editBooking.BookingReviewPage.header.bookingFor", {
                customerName: passengerText,
                bookingId: simulationData?.bookingId
              })}
            </div>
            <div className="tw:font-hanken-grotesk tw:text-[30px] tw:font-semibold tw:leading-normal tw:text-[#212529]">
              {t("editBooking.BookingReviewPage.header.reviewBooking")}
            </div>
          </div>
        </div>

        <section className="tw:w-[calc(100%+32px)] tw:-mx-4 tw:sm:mx-0 tw:sm:w-full tw:overflow-hidden">
          <div className="tw:flex tw:w-full">
            <div className="tw:w-[90px] tw:sm:w-[225px] tw:h-[68px] tw:bg-white tw:border-l tw:border-t tw:border-[#e8e8e8] tw:rounded-tl-[5px]" />
            <div className="tw:flex-1 tw:h-[68px] tw:bg-[#282828] tw:border-t tw:border-b tw:border-[#f9f9f9] tw:px-2 tw:sm:px-5 tw:flex tw:items-center">
              <div className="tw:text-white tw:text-[13px] tw:sm:text-[15px] tw:leading-[18px] tw:sm:leading-[22px] tw:font-bold">{t("editBooking.BookingReviewPage.before")}</div>
            </div>
            <div className="tw:flex-1 tw:h-[68px] tw:bg-[#282828] tw:border-t tw:border-b tw:border-l tw:border-[#f9f9f9] tw:px-2 tw:sm:px-5 tw:flex tw:items-center tw:rounded-tr-[5px]">
              <div className="tw:text-white tw:text-[13px] tw:sm:text-[15px] tw:leading-[18px] tw:sm:leading-[22px] tw:font-bold">{t("editBooking.BookingReviewPage.after")}</div>
            </div>
          </div>

          <SectionTitleRow title={t("editBooking.BookingReviewPage.generalInformation.title")} />

          <ComparisonRow
            label={t("editBooking.BookingReviewPage.generalInformation.transferType")}
            before={<ValueText text={formatTransferType(bookingTypeChange?.oldValue, oldBooking.isAirportTransfer)} muted={!bookingTypeChange?.oldValue} />}
            after={<ValueText text={formatTransferType(bookingTypeChange?.newValue, calculatedEditBooking.isAirportTransfer)} bold={bookingTypeChange?.changed} />}
            afterChanged={bookingTypeChange?.changed}
          />
          <ComparisonRow
            label={t("editBooking.BookingReviewPage.generalInformation.paxAndLuggage")}
            before={<ValuePaxText text={[formatPaxGroup(paxChange?.oldValue, t), formatLuggageGroup(luggageChange?.oldValue, t)].filter(Boolean)} />}
            after={<ValuePaxText text={[formatPaxGroup(paxChange?.newValue, t), formatLuggageGroup(luggageChange?.newValue, t)].filter(Boolean)} bold={paxChange?.changed || luggageChange?.changed} />}
            afterChanged={paxChange?.changed || luggageChange?.changed}
          />
          <ComparisonRow
            label={t("editBooking.BookingReviewPage.generalInformation.pickupLocation")}
            before={<ValueText text={getLocationDetail(pickupLocationChange?.oldValue)?.name} secondary={getLocationDetail(pickupLocationChange?.oldValue)?.address} />}
            after={<ValueText text={getLocationDetail(pickupLocationChange?.newValue)?.name} secondary={getLocationDetail(pickupLocationChange?.newValue)?.address} bold={pickupLocationChange?.changed} />}
            afterChanged={pickupLocationChange?.changed}
          />
          <div className="tw:flex tw:w-full">
            <LabelCell label={t("editBooking.BookingReviewPage.generalInformation.dropoffLocation")} />
            <div className="tw:flex tw:flex-col tw:flex-1">
              {(() => {
                const oldDests = Array.isArray(destinationsChange?.oldValue) ? destinationsChange.oldValue : [];
                const newDests = Array.isArray(destinationsChange?.newValue) ? destinationsChange.newValue : [];
                const maxRows = Math.max(oldDests.length, newDests.length, 1);

                return Array.from({ length: maxRows }).map((_, i) => {
                  const pair = {
                    old: oldDests[i] || null,
                    new: newDests[i] || null,
                  };
                  const oldLocation = pair.old ? getLocationDetail(pair.old) : null;
                  const newLocation = pair.new ? getLocationDetail(pair.new) : null;
                  const rowChanged = destinationsChange?.changed;
                  return (
                    <div key={i} className="tw:flex tw:flex-row tw:w-full tw:items-stretch">
                      <div className="tw:flex tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                        <ValueCell muted={!pair.old} className="tw:w-full">
                          {oldLocation ? <ValueText text={oldLocation.name} secondary={oldLocation.address} /> : null}
                        </ValueCell>
                      </div>
                      <div className="tw:flex tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                        <ValueCell changed={rowChanged && !!pair.new} isRight muted={!pair.new} className="tw:w-full">
                          {newLocation ? <ValueText text={newLocation.name} secondary={newLocation.address} bold={rowChanged} /> : null}
                        </ValueCell>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
          {
            (bookingTypeChange?.oldValue === "byTheHour" || bookingTypeChange?.newValue === "byTheHour") && (
              <>
                <ComparisonRow
                  label={t("editBooking.BookingReviewPage.generalInformation.usageDuration")}
                  before={<ValueText text={formatDuration(durationChange?.oldValue)} secondary={bookingTypeChange?.oldValue === "byTheHour" ? `${t("editBooking.BookingReviewPage.generalInformation.billedDuration")}: ${minuteToHoursFormat(billedDurationChange?.oldValue)}${t("editBooking.BookingReviewPage.generalInformation.hours")}` : undefined} muted={!durationChange?.oldValue} />}
                  after={<ValueText text={formatDuration(durationChange?.newValue)} secondary={bookingTypeChange?.newValue === "byTheHour" ? `${t("editBooking.BookingReviewPage.generalInformation.billedDuration")}: ${minuteToHoursFormat(billedDurationChange?.newValue)}${t("editBooking.BookingReviewPage.generalInformation.hours")}` : undefined} bold={durationChange?.changed} />}
                  afterChanged={durationChange?.changed}
                  beforeMuted={!durationChange?.oldValue}
                />
                <ComparisonRow
                  label={t("editBooking.BookingReviewPage.generalInformation.travelArea")}
                  before={<ValueText text={travelAreaChange?.oldValue ? t("myPage.booking.bookingItem.travelArea." + travelAreaChange.oldValue) : "-"} muted={!travelAreaChange?.oldValue} />}
                  after={<ValueText text={travelAreaChange?.newValue ? t("myPage.booking.bookingItem.travelArea." + travelAreaChange.newValue) : "-"} bold={travelAreaChange?.changed} />}
                  afterChanged={travelAreaChange?.changed}
                  beforeMuted={!travelAreaChange?.oldValue}
                />
              </>
            )
          }
          <ComparisonRow
            label={t("editBooking.BookingReviewPage.generalInformation.pickupTime")}
            before={<ValueText text={moment(pickupDateTimeChange?.oldValue).format(DATE_TIME_FORMAT[currentLanguage])} />}
            after={<ValueText text={moment(pickupDateTimeChange?.newValue).format(DATE_TIME_FORMAT[currentLanguage])} bold={pickupDateTimeChange?.changed} />}
            afterChanged={pickupDateTimeChange?.changed}
          />
          <ComparisonRow
            label={t("editBooking.BookingReviewPage.generalInformation.estDropoffTime")}
            before={<ValueText text={moment(customerDropoffDateTimeChange?.oldValue).format(DATE_TIME_FORMAT[currentLanguage]) || "-"} muted={!customerDropoffDateTimeChange?.oldValue} />}
            after={<ValueText text={moment(customerDropoffDateTimeChange?.newValue).format(DATE_TIME_FORMAT[currentLanguage]) || "-"} bold={customerDropoffDateTimeChange?.changed} />}
            afterChanged={customerDropoffDateTimeChange?.changed}
            beforeMuted={!customerDropoffDateTimeChange?.oldValue}
          />

          <SectionTitleRow title={t("editBooking.BookingReviewPage.vehicleSelection.title")} />

          {vehiclePairs.map(({ before: vehicleBefore, after: vehicleAfter }, i) => {
            const vehicleChanged = (formatVehicleTypeName(vehicleBefore) !== formatVehicleTypeName(vehicleAfter));

            return (
              <ComparisonRow
                key={i}
                label={t("editBooking.BookingReviewPage.vehicleSelection.vehicle", {
                  number: vehiclePairs.length > 1 ? i + 1 : ""
                })}
                before={vehicleBefore ? <VehicleCell vehicleClass={formatVehicleTagName(vehicleBefore)} vehicleType={formatVehicleTypeName(vehicleBefore)} vehicleImage={vehicleBefore?.vehicleTypeID?.images?.png} note={vehicleBefore?.vehicleTypeName === "Toyota Alphard" ? t("editBooking.BookingReviewPage.vehicleSelection.alphardNote") : undefined} /> : "-"}
                after={vehicleAfter ? <VehicleCell vehicleClass={formatVehicleTagName(vehicleAfter)} vehicleType={formatVehicleTypeName(vehicleAfter)} vehicleImage={vehicleAfter?.vehicleTypeID?.images?.png} bold={vehicleChanged} note={vehicleAfter?.vehicleTypeName === "Toyota Alphard" ? t("editBooking.BookingReviewPage.vehicleSelection.alphardNote") : undefined} /> : "-"}
                afterChanged={vehicleChanged}
                beforeMuted={!vehicleBefore}
                afterMuted={!vehicleAfter}
              />
            );
          })}

          <SectionTitleRow title={t("editBooking.BookingReviewPage.passengerInformation.title")} />

          <ComparisonRow
            label={t("editBooking.BookingReviewPage.passengerInformation.passengerName")}
            before={<ValueText text={formatPassengerName(oldPassengerTitle, oldPassengerName)} muted={!oldPassengerName && !oldPassengerTitle} />}
            after={<ValueText text={formatPassengerName(newPassengerTitle, newPassengerName)} bold={passengerNameChanged} />}
            afterChanged={passengerNameChanged}
            beforeMuted={!oldPassengerName && !oldPassengerTitle}
          />

          <ComparisonRow
            label={t("editBooking.BookingReviewPage.passengerInformation.passengerPhoneNumber")}
            before={<ValueText text={formatPassengerPhone(oldPassengerPrefix, oldPassengerPhone)} muted={!oldPassengerPhone} />}
            after={<ValueText text={formatPassengerPhone(newPassengerPrefix, newPassengerPhone)} bold={passengerPhoneChanged} />}
            afterChanged={passengerPhoneChanged}
            beforeMuted={!oldPassengerPhone}
          />

          <SectionTitleRow title={t("editBooking.BookingReviewPage.additionalInformation.title")} />

          {
            ((oldBooking.isAirportTransfer || calculatedEditBooking.isAirportTransfer) || (oldBooking.isStationTransfer || calculatedEditBooking.isStationTransfer)) && (
              <>
                {(oldBooking.isFromAirport || calculatedEditBooking.isFromAirport || oldBooking.isFromStation || calculatedEditBooking.isFromStation) && (
                  <>
                    <ComparisonRow
                      label={t(`editBooking.BookingReviewPage.additionalInformation.pickupFlightNumber.${pickupFlightLabelType}`)}
                      before={<ValueText text={pickupFlightNoChange?.oldValue || "-"} muted={!pickupFlightNoChange?.oldValue} />}
                      after={<ValueText text={pickupFlightNoChange?.newValue || "-"} bold={pickupFlightNoChange?.changed} />}
                      afterChanged={pickupFlightNoChange?.changed}
                      beforeMuted={!pickupFlightNoChange?.oldValue}
                    />
                    {
                      (oldBooking.isFromAirport || calculatedEditBooking.isFromAirport) && (
                        <>
                          <ComparisonRow
                            label={t("editBooking.BookingReviewPage.additionalInformation.pickupTerminal")}
                            before={<ValueText text={pickupTerminalChange?.oldValue && pickupTerminalChange?.oldValue !== "" ? t(`bookingDetail.bookingItem.terminal.${pickupTerminalChange?.oldValue}`) : "-"} muted={!pickupTerminalChange?.oldValue} />}
                            after={<ValueText text={pickupTerminalChange?.newValue && pickupTerminalChange?.newValue !== "" ? t(`bookingDetail.bookingItem.terminal.${pickupTerminalChange?.newValue}`) : "-"} bold={pickupTerminalChange?.changed} />}
                            afterChanged={pickupTerminalChange?.changed}
                            beforeMuted={!pickupTerminalChange?.oldValue}
                          />
                          <ComparisonRow
                            label={t("editBooking.BookingReviewPage.additionalInformation.pickupSign.airport")}
                            before={<ValueText text={getPickupSignText(pickupSignHonorificChange?.oldValue, pickupSignChange?.oldValue)} muted={!pickupSignChange?.oldValue} />}
                            after={<ValueText text={getPickupSignText(pickupSignHonorificChange?.newValue, pickupSignChange?.newValue)} bold={pickupSignChange?.changed || pickupSignHonorificChange?.changed} />}
                            afterChanged={pickupSignChange?.changed || pickupSignHonorificChange?.changed}
                            beforeMuted={!pickupSignChange?.oldValue}
                          />
                        </>
                      )
                    }
                  </>
                )}

                {(oldBooking.isToAirport || calculatedEditBooking.isToAirport || oldBooking.isToStation || calculatedEditBooking.isToStation) && (
                  <>
                    <ComparisonRow
                      label={t(`editBooking.BookingReviewPage.additionalInformation.dropoffFlightNumber.${dropoffFlightLabelType}`)}
                      before={<ValueText text={dropoffFlightNoChange?.oldValue || "-"} muted={!dropoffFlightNoChange?.oldValue} />}
                      after={<ValueText text={dropoffFlightNoChange?.newValue || "-"} bold={dropoffFlightNoChange?.changed} />}
                      afterChanged={dropoffFlightNoChange?.changed}
                      beforeMuted={!dropoffFlightNoChange?.oldValue}
                    />
                    {
                      (oldBooking.isToAirport || calculatedEditBooking.isToAirport) && (
                        <ComparisonRow
                          label={t("editBooking.BookingReviewPage.additionalInformation.dropoffTerminal")}
                          before={<ValueText text={dropoffTerminalChange?.oldValue && dropoffTerminalChange?.oldValue !== "" ? t(`bookingDetail.bookingItem.terminal.${dropoffTerminalChange?.oldValue}`) : "-"} muted={!dropoffTerminalChange?.oldValue} />}
                          after={<ValueText text={dropoffTerminalChange?.newValue && dropoffTerminalChange?.newValue !== "" ? t(`bookingDetail.bookingItem.terminal.${dropoffTerminalChange?.newValue}`) : "-"} bold={dropoffTerminalChange?.changed} />}
                          afterChanged={dropoffTerminalChange?.changed}
                          beforeMuted={!dropoffTerminalChange?.oldValue}
                        />
                      )
                    }
                  </>
                )}

                <ComparisonRow
                  label={t("editBooking.BookingReviewPage.additionalInformation.meetAssistService")}
                  before={<ValueText text={formatMeetAssist(meetAssistChange?.oldValue).title} />}
                  after={<ValueText text={formatMeetAssist(meetAssistChange?.newValue).title} bold={meetAssistChange?.changed} />}
                  afterChanged={meetAssistChange?.changed}
                />
              </>
            )
          }
          <div className="tw:flex tw:w-full">
            <LabelCell label={t("editBooking.BookingReviewPage.additionalInformation.addOnServices")} />
            <div className="tw:flex tw:flex-col tw:flex-1">
              {vehiclePairs.map(({ before: vehicleBefore, after: vehicleAfter }, i) => {
                const showVehicleLabel = vehiclePairs.length > 1;
                const addonChanged = (() => {
                  if (!vehicleBefore && !vehicleAfter) return false;
                  if (!vehicleBefore || !vehicleAfter) return true;
                  const beforeSeats = JSON.stringify((vehicleBefore.childSeat || []).map(s => ({ type: s.childSeatType, count: s.childSeatCount })));
                  const afterSeats = JSON.stringify((vehicleAfter.childSeat || []).map(s => ({ type: s.childSeatType, count: s.childSeatCount })));
                  return beforeSeats !== afterSeats;
                })();
                return (
                  <div key={i} className="tw:flex tw:flex-row tw:w-full tw:items-stretch">
                    <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1">
                      {showVehicleLabel && (
                        <div className={cn(
                          "tw:w-full tw:lg:w-[30%] tw:flex tw:items-center tw:justify-center tw:p-2 tw:border-b tw:lg:border-r tw:border-l tw:border-[#e8e8e8]",
                          vehicleBefore ? "tw:bg-[#f9f9f9]" : "tw:bg-[#f3f3f3]"
                        )}>
                          <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-normal tw:text-center tw:break-word">
                            {vehicleBefore ? formatVehicleTypeName(vehicleBefore) : "-"}
                          </div>
                        </div>
                      )}
                      <div className="tw:flex-1 tw:flex">
                        <ValueCell muted={!vehicleBefore} className={cn("tw:w-full", showVehicleLabel && "tw:border-l-0 tw:lg:border-l")}>
                          {vehicleBefore && <PriceDetailsCell title={t("editBooking.BookingReviewPage.additionalInformation.childSeat")} details={getVehicleAddonDetails(vehicleBefore)} />}
                        </ValueCell>
                      </div>
                    </div>
                    <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1">
                      {showVehicleLabel && (
                        <div className={cn(
                          "tw:w-full tw:lg:w-[30%] tw:flex tw:items-center tw:justify-center tw:p-2 tw:border-b tw:lg:border-r tw:border-l tw:border-[#e8e8e8]",
                          vehicleAfter ? (addonChanged ? "tw:bg-[#fff8e8]" : "tw:bg-[#f9f9f9]") : "tw:bg-[#f3f3f3]"
                        )}>
                          <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-normal tw:text-center tw:break-word">
                            {vehicleAfter ? formatVehicleTypeName(vehicleAfter) : "-"}
                          </div>
                        </div>
                      )}
                      <div className="tw:flex-1 tw:flex">
                        <ValueCell changed={addonChanged && !!vehicleAfter} isRight muted={!vehicleAfter} className={cn("tw:w-full", showVehicleLabel && "tw:border-l-0 tw:lg:border-l")}>
                          {vehicleAfter && <PriceDetailsCell title={t("editBooking.BookingReviewPage.additionalInformation.childSeat")} details={getVehicleAddonDetails(vehicleAfter)} highlight bold={addonChanged} />}
                        </ValueCell>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <ComparisonRow
            label={t("editBooking.BookingReviewPage.additionalInformation.specialRequest")}
            before={<ValueText text={formatMessage(specialRequestChange?.oldValue) || "-"} />}
            after={<ValueText text={formatMessage(specialRequestChange?.newValue) || "-"} bold={specialRequestChange?.changed} />}
            afterChanged={specialRequestChange?.changed}
          />

          <SectionTitleRow title={t("editBooking.BookingReviewPage.pricing.title")} />

          <div className="tw:flex tw:w-full">
            <LabelCell label={t("editBooking.BookingReviewPage.pricing.detail")} />
            <div className="tw:flex tw:flex-col tw:flex-1">
              {vehiclePairs.map(({ before: vehicleBefore, after: vehicleAfter }, i) => {
                const showVehicleLabel = vehiclePairs.length > 1;
                const changed = isVehiclePricingChanged(vehicleBefore, vehicleAfter);
                return (
                  <div key={i} className="tw:flex tw:flex-row tw:w-full tw:items-stretch">
                    <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                      {showVehicleLabel && (
                        <div className={cn(
                          "tw:w-full tw:lg:w-[30%] tw:flex tw:items-center tw:justify-center tw:p-2 tw:border-b tw:lg:border-r tw:border-l tw:border-[#e8e8e8]",
                          vehicleBefore ? "tw:bg-[#f9f9f9]" : "tw:bg-[#f3f3f3]"
                        )}>
                          <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-normal tw:text-center tw:break-word">
                            {vehicleBefore ? formatVehicleTypeName(vehicleBefore) : "-"}
                          </div>
                        </div>
                      )}
                      <div className="tw:flex-1 tw:flex">
                        <ValueCell muted={!vehicleBefore} className={cn("tw:w-full", showVehicleLabel && "tw:border-l-0 tw:lg:border-l")}>
                          {vehicleBefore && <PriceDetailsCell details={getVehiclePriceDetails(vehicleBefore)} />}
                        </ValueCell>
                      </div>
                    </div>
                    <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                      {showVehicleLabel && (
                        <div className={cn(
                          "tw:w-full tw:lg:w-[30%] tw:flex tw:items-center tw:justify-center tw:p-2 tw:border-b tw:lg:border-r tw:border-l tw:border-[#e8e8e8]",
                          vehicleAfter ? (changed ? "tw:bg-[#fff8e8]" : "tw:bg-[#f9f9f9]") : "tw:bg-[#f3f3f3]"
                        )}>
                          <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-normal tw:text-center tw:break-word">
                            {vehicleAfter ? formatVehicleTypeName(vehicleAfter) : "-"}
                          </div>
                        </div>
                      )}
                      <div className="tw:flex-1 tw:flex">
                        <ValueCell changed={changed && !!vehicleAfter} isRight muted={!vehicleAfter} className={cn("tw:w-full", showVehicleLabel && "tw:border-l-0 tw:lg:border-l")}>
                          {vehicleAfter && <PriceDetailsCell details={getVehiclePriceDetails(vehicleAfter)} highlight bold={changed} />}
                        </ValueCell>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="tw:flex tw:flex-row tw:w-full tw:items-stretch">
                <div className="tw:flex tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                  <ValueCell>
                    <PriceDetailsCell
                      details={[
                        { label: t("editBooking.BookingReviewPage.pricing.meetAssistService"), value: formatJPY(totalMeetAssistPriceChange?.oldValue) },
                      ]}
                    />
                  </ValueCell>
                </div>
                <div className="tw:flex tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                  <ValueCell changed={totalMeetAssistPriceChange?.changed} isRight>
                    <PriceDetailsCell
                      highlight
                      details={[
                        { label: t("editBooking.BookingReviewPage.pricing.meetAssistService"), value: formatJPY(totalMeetAssistPriceChange?.newValue) },
                      ]}
                    />
                  </ValueCell>
                </div>
              </div>
            </div>
          </div>

          <div className="tw:flex tw:w-full">
            <LabelCell label={t("editBooking.BookingReviewPage.pricing.totalPrice")} isFirst />
            <ValueCell>
              <div className="tw:flex tw:flex-col tw:sm:flex-row tw:items-start tw:sm:items-center tw:justify-between tw:w-full tw:gap-1 tw:sm:gap-0">
                <div className="tw:text-[#8d8d8d] tw:text-[12px] tw:sm:text-[14px] tw:leading-[18px] tw:sm:leading-[20px] tw:italic">{getOldBookingPaymentInfo(oldBooking, userDeferredPaymentEligible)}</div>
                <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[16px] tw:leading-[20px] tw:sm:leading-[22px] tw:font-medium">{formatJPY(totalPriceChange?.oldValue)}</div>
              </div>
            </ValueCell>
            <ValueCell changed={totalPriceChange?.changed} isRight isLast>
              <div className="tw:flex tw:flex-col tw:items-end tw:gap-[3px] tw:w-full">
                <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[16px] tw:leading-[20px] tw:sm:leading-[22px] tw:font-medium">
                  {formatJPY(totalPriceChange?.newValue)}
                </div>
                {totalPriceChange?.changed && (
                  <div className={"tw:flex tw:items-center tw:gap-1 tw:text-[13px] tw:leading-[18.5px] tw:font-bold tw:text-[#ea4335]"}>
                    <span className="tw-flex tw-items-center tw-h-full">{diffPrice > 0 ? "+" : ""}{formatJPY(diffPrice)}</span>
                    {diffPrice > 0 ? <ArrowCircleUpIcon color="#EA4335" size={15} /> : <ArrowCircleDownIcon color="#EA4335" size={15} />}
                  </div>
                )}
              </div>
            </ValueCell>
          </div>
        </section>

        {
          (editFeeCalculation.totalPriceCancellation > 0 || editFeeCalculation.totalMeetAssistCancellation > 0) && (
            <CancellationFeeInformation
              getTypographyClass={getTypographyClass}
              editFeeCalculation={editFeeCalculation}
              newBookingTotalPrice={calculatedEditBooking.totalPrice}
              paymentStatus={bookingPaymentStatus}
              deferredPayment={userDeferredPaymentEligible}
              openPolicyModal={() => setShowPolicyModal(true)}
              changes={changes}
              oldBookingPrice={oldBooking.totalPrice}
              oldBookingMeetAssist={oldBooking.meetAssist}
              meetAssistChangesIdentification={meetAssistChange}
            />
          )
        }

        <ImportantNotice getTypographyClass={getTypographyClass} deferredPayment={userDeferredPaymentEligible} />

        <div className="tw:flex tw:flex-col tw:lg:flex-row tw:items-center tw:justify-between tw:w-full tw:gap-8 tw:lg:gap-0">
          <div className="tw:flex tw:flex-col tw:sm:flex-row tw:items-center tw:gap-2 tw:w-full tw:lg:w-auto">
            <button type="button" className="tw:h-[45px] tw:w-full tw:lg:w-[300px]" onClick={onBackToEdit || (() => { })} disabled={isSubmittingModification}>
              <div className="tw:h-full tw:w-full tw:rounded-[5px] tw:bg-[#F3F3F3] tw:text-[#282828] tw:flex tw:items-center tw:justify-center tw:text-[15px] tw:font-[400] tw:leading-[22px] tw:font-hanken-grotesk">
                {t("editBooking.BookingReviewPage.buttons.edit")}
              </div>
            </button>
            <button type="button" className="tw:h-[45px] tw:w-full tw:lg:w-[300px]" onClick={() => setShowSubmitModal(true)} disabled={isSubmittingModification}>
              <div className={`tw:h-full tw:w-full tw:rounded-[5px] tw:bg-[#DBBE78] tw:text-white tw:flex tw:items-center tw:justify-center tw:gap-2 tw:text-[15px] tw:font-[400] tw:leading-[22px] tw:font-hanken-grotesk ${isSubmittingModification ? "tw:opacity-70" : ""}`}>
                {isSubmittingModification && (
                  <svg className="tw:animate-spin tw:h-4 tw:w-4 tw:text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="tw:opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                )}
                {oldBooking.paymentStatus === "CHARGE_PENDING"
                  ? t("editBooking.BookingReviewPage.buttons.updateBookingDetails")
                  : t("editBooking.BookingReviewPage.buttons.submitModificationRequest")}
              </div>
            </button>
            {/* <button type="button" className="tw:h-[45px] tw:w-full tw:lg:w-[300px]" onClick={() => setShowDraftModal(true)} disabled={isSubmittingModification}>
              <div className="tw:h-full tw:w-full tw:rounded-[5px] tw:bg-[#7C7B7B] tw:text-white tw:flex tw:items-center tw:justify-center tw:text-[15px] tw:font-[400] tw:leading-[22px] tw:font-hanken-grotesk">
                Save as Draft
              </div>
            </button> */}
          </div>

          <button type="button" className="tw:h-[45px] tw:w-full tw:lg:w-auto" onClick={() => setShowDiscardModal(true)} disabled={isSubmittingModification}>
            <div className="tw:h-full tw:w-full tw:px-6 tw:rounded-[5px] tw:border tw:border-[#ea4335] tw:bg-white tw:text-[#ea4335] tw:flex tw:items-center tw:justify-center tw:gap-2 tw:text-[15px] tw:leading-[22px] tw:font-hanken-grotesk">
              <CancelIcon color="#EA4335" size={14} />
              {t("editBooking.BookingReviewPage.buttons.discardEdit")}
            </div>
          </button>
        </div>

        <SubmitEditConfirmationModal
          show={showSubmitModal}
          onClose={() => setShowSubmitModal(false)}
          editFeeCalculation={editFeeCalculation}
          calculatedEditBooking={calculatedEditBooking}
          userDeferredPaymentEligible={userDeferredPaymentEligible}
          handleSubmitModification={handleSubmitModification}
          isSubmittingModification={isSubmittingModification}
          oldBooking={oldBooking}
          needsCharge={needsCharge}
          role={userRole}
          getTypographyClass={getTypographyClass}
          cn={cn}
        />

        <SaveDraftConfirmationModal
          show={showDraftModal}
          onClose={() => setShowDraftModal(false)}
          onConfirm={handleSaveDraft}
          isSaving={isSubmittingModification}
        />

        <DiscardEditConfirmationModal
          show={showDiscardModal}
          onClose={() => setShowDiscardModal(false)}
          handleDiscardEdit={handleDiscardEdit}
          isSubmittingModification={isSubmittingModification}
        />

        <SuccessSubmitEditBookingModal
          show={showSubmitSuccessModal}
          onClose={handleCloseSubmitSuccessModal}
          oldBooking={oldBooking}
          editFeeCalculation={editFeeCalculation}
          calculatedEditBooking={calculatedEditBooking}
          userDeferredPaymentEligible={userDeferredPaymentEligible}
        />

        <CancellationPolicyModal
          show={showPolicyModal}
          onClose={() => setShowPolicyModal(false)}
          role={userRole}
        />
      </div>
    </div >
  );

  return <FrontLayout>{content}</FrontLayout>;
};

export default BookingEditReview;
